'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { netlifyFormMessage, type NetlifyStatus } from '@/components/use-netlify-form';
const HEADINGS: Record<string, {
  success: string;
  error: string;
}> = {
  fr: {
    success: 'Message envoyé !',
    error: 'Une erreur est survenue'
  },
  en: {
    success: 'Message sent!',
    error: 'Something went wrong'
  },
  nl: {
    success: 'Bericht verzonden!',
    error: 'Er ging iets mis'
  },
  es: {
    success: '¡Mensaje enviado!',
    error: 'Algo salió mal'
  },
  it: {
    success: 'Messaggio inviato!',
    error: 'Si è verificato un errore'
  }
};

/**
 * Prominent, on-brand confirmation shown after a contact form is submitted.
 * Success = green with a check medallion; error = red with an alert medallion.
 */
export function FormStatus(t0) {
  const $ = _c(26);
  const {
    status,
    locale
  } = t0;
  const ref = useRef(null);
  const active = status === "success" || status === "error";
  let t1;
  let t2;
  if ($[0] !== active) {
    t1 = () => {
      if (active) {
        ref.current?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    };
    t2 = [active];
    $[0] = active;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!active) {
    return null;
  }
  const isSuccess = status === "success";
  const Icon = isSuccess ? CheckCircle2 : AlertCircle;
  const h = HEADINGS[locale] || HEADINGS.en;
  const t3 = isSuccess ? "border-green-500/40 bg-green-50 text-green-900" : "border-red-400/50 bg-red-50 text-red-900";
  let t4;
  if ($[3] !== t3) {
    t4 = cn("mt-5 flex items-center gap-4 rounded-2xl border-2 p-5 shadow-lg", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = isSuccess ? "bg-green-600 text-white" : "bg-red-600 text-white";
  let t6;
  if ($[5] !== t5) {
    t6 = cn("flex size-12 shrink-0 items-center justify-center rounded-full shadow-md", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== Icon) {
    t7 = <Icon className="size-7" />;
    $[7] = Icon;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <span className={t6}>{t7}</span>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = isSuccess ? h.success : h.error;
  let t10;
  if ($[12] !== t9) {
    t10 = <p className="text-lg font-bold leading-tight">{t9}</p>;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== locale || $[15] !== status) {
    t11 = netlifyFormMessage(locale, status);
    $[14] = locale;
    $[15] = status;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11) {
    t12 = <p className="mt-0.5 text-sm font-medium opacity-90">{t11}</p>;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t10 || $[20] !== t12) {
    t13 = <div className="min-w-0">{t10}{t12}</div>;
    $[19] = t10;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t4 || $[24] !== t8) {
    t14 = <div ref={ref} role="status" aria-live="polite" className={t4}>{t8}{t13}</div>;
    $[22] = t13;
    $[23] = t4;
    $[24] = t8;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  return t14;
}